import React, { useState, useEffect } from 'react';

// Enhanced Image Gallery Modal Component with Navigation
export const ImagePreviewModal = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  altText, 
  title,
  images = [], // Array of image URLs for gallery navigation
  currentIndex = 0, // Current image index in the gallery
  onNavigate // Callback when navigating between images
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset zoom and position when image changes
  useEffect(() => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, [imageUrl, currentIndex]);

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    if (!isOpen) return;

    const handleWheel = (e) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        // Scroll up - zoom in
        setZoomLevel(prev => Math.min(prev + 0.5, 4));
      } else {
        // Scroll down - zoom out
        setZoomLevel(prev => {
          const newZoom = Math.max(prev - 0.5, 1);
          // Reset position when zooming out to 1x
          if (newZoom === 1) {
            setPosition({ x: 0, y: 0 });
          }
          return newZoom;
        });
      }
    };

    const imageContainer = document.getElementById('image-lightbox-container');
    if (imageContainer) {
      imageContainer.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        imageContainer.removeEventListener('wheel', handleWheel);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasMultipleImages = images && images.length > 1;
  const canGoPrevious = hasMultipleImages && currentIndex > 0;
  const canGoNext = hasMultipleImages && currentIndex < images.length - 1;

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
    // Reset position when zooming out to 1x
    if (zoomLevel <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handlePrevious = () => {
    if (canGoPrevious && onNavigate) {
      // Reset zoom and position before navigating
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && onNavigate) {
      // Reset zoom and position before navigating
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      onNavigate(currentIndex + 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === '=' || e.key === '+') {
      handleZoomIn();
    } else if (e.key === '-') {
      handleZoomOut();
    } else if (e.key === '0') {
      handleResetZoom();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Large X Close Button - Top Right */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 z-50 bg-red-600 hover:bg-red-700 text-white p-4 rounded-full shadow-2xl transition-all duration-200 hover:scale-125 cursor-pointer"
        title="Close"
        style={{ fontSize: '24px', fontWeight: 'bold' }}
      >
        ✕
      </button>

      {/* Close Button with Text - Top Center (Mobile Friendly) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 left-1/2 transform -translate-x-1/2 z-50 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full shadow-2xl transition-all duration-200 hover:scale-105 cursor-pointer font-bold text-lg flex items-center gap-2"
        title="Close lightbox"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
        CLOSE
      </button>

      <div 
        className="relative max-w-7xl max-h-screen w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Zoom Controls - Floating Top Left */}
        <div className="absolute top-4 left-4 z-40 flex items-center space-x-2 bg-black bg-opacity-60 rounded-lg p-2 shadow-xl">
          <span className="text-sm text-white font-medium px-2">{Math.round(zoomLevel * 100)}%</span>
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            className={`text-white p-2 rounded transition-all ${
              zoomLevel <= 1 
                ? 'opacity-40 cursor-not-allowed' 
                : 'hover:bg-white hover:bg-opacity-20'
            }`}
            title="Zoom Out (- or scroll down)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <button
            onClick={handleResetZoom}
            disabled={zoomLevel === 1}
            className={`text-white px-3 py-2 rounded text-sm font-medium transition-all ${
              zoomLevel === 1 
                ? 'opacity-40 cursor-not-allowed' 
                : 'hover:bg-white hover:bg-opacity-20'
            }`}
            title="Reset Zoom (0)"
          >
            Reset
          </button>
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 4}
            className={`text-white p-2 rounded transition-all ${
              zoomLevel >= 4 
                ? 'opacity-40 cursor-not-allowed' 
                : 'hover:bg-white hover:bg-opacity-20'
            }`}
            title="Zoom In (+ or scroll up)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
        </div>

        {/* Title - Floating Top Center */}
        {title && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-40 bg-black bg-opacity-60 rounded-lg px-6 py-2 shadow-xl">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
          </div>
        )}

        {/* Image Container */}
        <div 
          id="image-lightbox-container"
          className="flex-1 bg-black flex items-center justify-center overflow-hidden relative"
        >
          {/* Previous Button */}
          {canGoPrevious && (
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 p-4 rounded-full transition-all z-20 shadow-2xl hover:scale-110"
              title="Previous Image (←)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next Button */}
          {canGoNext && (
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 p-4 rounded-full transition-all z-20 shadow-2xl hover:scale-110"
              title="Next Image (→)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Image with Zoom */}
          <div
            className="relative"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
              transition: isDragging ? 'none' : 'transform 0.3s ease',
              cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={imageUrl}
              alt={altText}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              style={{ 
                maxHeight: '90vh',
                maxWidth: '90vw'
              }}
            />
          </div>

          {/* Image Counter */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-6 py-2 rounded-full text-base font-medium shadow-xl">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* Zoom Hint */}
          {zoomLevel === 1 && (
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white text-sm px-4 py-2 rounded-lg shadow-xl">
              💡 Scroll to zoom
            </div>
          )}
        </div>

        {/* Footer - Help Text with Close Button */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex-1 text-center">
            <p className="text-sm">
              🖱️ <strong>Scroll</strong> to zoom • 
              {zoomLevel > 1 && ' 👆 <strong>Drag</strong> to pan • '}
              {hasMultipleImages && ' ← → <strong>Arrow keys</strong> or buttons to navigate'}
            </p>
          </div>
          
          {/* Close Button in Footer */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold transition-all hover:scale-105 cursor-pointer flex items-center gap-2 shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

// Image Thumbnail with Click to Preview
export const ImageThumbnail = ({ 
  imageUrl, 
  altText, 
  title, 
  className = "", 
  onClick,
  images = [], // Optional: array of all images for gallery mode
  imageIndex = 0 // Optional: index of this image in the gallery
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(imageIndex);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      setCurrentImageIndex(imageIndex);
      setShowPreview(true);
    }
  };

  const handleNavigate = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  // Determine the current image URL
  const currentImageUrl = images.length > 0 ? images[currentImageIndex] : imageUrl;

  return (
    <>
      <img
        src={imageUrl}
        alt={altText}
        className={`cursor-pointer hover:opacity-80 transition-opacity ${className}`}
        onClick={handleClick}
        title="Click to view larger image"
      />
      
      <ImagePreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        imageUrl={currentImageUrl}
        altText={altText}
        title={title}
        images={images}
        currentIndex={currentImageIndex}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default ImagePreviewModal;